import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { GraduationCap, TrendingUp, AlertCircle } from "lucide-react";

export default function Eligibility() {
  const { data: eligibilityRecords = [], isLoading } = useQuery({
    queryKey: ["eligibility-tracking"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("eligibility_tracking")
        .select(`
          *,
          athletes (name, sport, year)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const stats = {
    total: eligibilityRecords.length,
    eligible: eligibilityRecords.filter((r) => r.eligibility_status === "eligible").length,
    provisional: eligibilityRecords.filter((r) => r.eligibility_status === "provisional").length,
    ineligible: eligibilityRecords.filter((r) => r.eligibility_status === "ineligible").length,
    underReview: eligibilityRecords.filter((r) => r.eligibility_status === "under_review").length,
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "eligible": return "default";
      case "provisional": return "secondary";
      case "ineligible": return "destructive";
      case "under_review": return "outline";
      default: return "outline";
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <GraduationCap className="h-8 w-8 text-primary" />
            Eligibility & Academic Tracking
          </h1>
          <p className="text-muted-foreground mt-2">
            Monitor athlete academic progress and NCAA eligibility requirements
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Athletes</CardTitle>
              <GraduationCap className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-xs text-muted-foreground">Being tracked</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Eligible</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.eligible}</div>
              <p className="text-xs text-muted-foreground">Fully eligible</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Provisional</CardTitle>
              <AlertCircle className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{stats.provisional}</div>
              <p className="text-xs text-muted-foreground">Conditional status</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Needs Review</CardTitle>
              <AlertCircle className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">{stats.ineligible + stats.underReview}</div>
              <p className="text-xs text-muted-foreground">Action required</p>
            </CardContent>
          </Card>
        </div>

        {/* Eligibility Table */}
        <Card>
          <CardHeader>
            <CardTitle>Athlete Eligibility Records</CardTitle>
            <CardDescription>Academic progress and eligibility status for all athletes</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-12 text-muted-foreground">Loading eligibility data...</div>
            ) : eligibilityRecords.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No eligibility records found. Demo data may not be loaded.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Athlete</TableHead>
                    <TableHead>Sport</TableHead>
                    <TableHead>Year</TableHead>
                    <TableHead>Academic Year</TableHead>
                    <TableHead>Current GPA</TableHead>
                    <TableHead>Cumulative GPA</TableHead>
                    <TableHead>Credits</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {eligibilityRecords.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell className="font-medium">{record.athletes?.name}</TableCell>
                      <TableCell>{record.athletes?.sport}</TableCell>
                      <TableCell>{record.athletes?.year}</TableCell>
                      <TableCell>{record.academic_year}</TableCell>
                      <TableCell>{record.current_gpa?.toFixed(2) || 'N/A'}</TableCell>
                      <TableCell>{record.cumulative_gpa?.toFixed(2) || 'N/A'}</TableCell>
                      <TableCell>{record.credits_completed}/{record.credits_enrolled}</TableCell>
                      <TableCell>{record.degree_progress_percentage}%</TableCell>
                      <TableCell>
                        <Badge variant={getStatusColor(record.eligibility_status)}>
                          {record.eligibility_status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
